/**
 * Customer Allocation List — mirrors ApAllocationListPage with partyType = 'Customer'
 * and AR permission checks.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Plus, RefreshCw, FileText, CheckCircle2,
  Clock, RotateCcw, Download,
} from 'lucide-react';

import { allocationApi }  from '@/api/allocation.api';
import { exportToCsv }   from '@/utils/exportCsv';
import { usePermission }  from '@/hooks/usePermission';
import { Permissions }    from '@/constants/permissions';
import { AppRoutes }      from '@/constants/routes';
import { Button }        from '@/components/ui/Button';
import { IconButton }    from '@/components/ui/IconButton';
import { PageHeader }    from '@/components/layout/PageHeader';
import { ListTable, type ListColumn } from '@/components/lists/ListTable';
import { ScopeChips }     from '@/components/lists/ScopeChips';
import {
  FilterRow, ListSearch, DateRangeFilter, StatusPills, StatusTiles,
} from '@/components/lists/ListFilters';
import { ListPager }      from '@/components/lists/ListPager';
import { useContextScope, scopeFromSession } from '@/hooks/useContextScope';
import { cn }             from '@/utils/cn';
import type { RootState } from '@/state/store';
import type {
  AllocationListDto,
  AllocationFilterDto,
  AllocationWorkflowStatus,
} from '@/types/allocation.types';

const STATUS_LABELS: Record<AllocationWorkflowStatus, string> = {
  Draft: 'Draft', Submitted: 'Submitted', Approved: 'Approved',
  Confirmed: 'Confirmed', Reversed: 'Reversed', Cancelled: 'Cancelled',
};
const STATUS_COLORS: Record<AllocationWorkflowStatus, string> = {
  Draft:     'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  Submitted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  Approved:  'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  Confirmed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  Reversed:  'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  Cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const fmt     = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });

// Allocations were never filtered by financial year, only by location and cost centre.
const SCOPE_KEYS = ['locationId', 'costCenterId'] as const;

export const ArAllocationListPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate         = hasPermission(Permissions.AR_ALLOCATION_CREATE);

  const session = useSelector((s: RootState) => s.session.session);

  const [items,   setItems]   = useState<AllocationListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [filter, setFilter] = useState<AllocationFilterDto>(() => ({
    partyType:      'Customer',
    pageNumber:     1,
    pageSize,
    sortDescending: true,
    ...scopeFromSession(session, SCOPE_KEYS),
  }));

  const [search, setSearch] = useState('');

  const scope = useContextScope(sc => {
    setFilter(f => ({ ...f, ...sc, pageNumber: 1 }));
    setPage(1);
  }, SCOPE_KEYS);

  const load = useCallback(async (f: AllocationFilterDto) => {
    setLoading(true);
    try {
      const res  = await allocationApi.getList(f);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch (e) { console.error('Failed to load AR allocations', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(filter); }, [filter, load]);

  const handleSearch       = () => { setFilter(f => ({ ...f, allocationNumber: search || undefined, pageNumber: 1 })); setPage(1); };
  const handleStatusFilter = (s: AllocationWorkflowStatus | undefined) => { setFilter(f => ({ ...f, workflowStatus: s, pageNumber: 1 })); setPage(1); };
  const handlePageChange   = (p: number) => { setPage(p); setFilter(f => ({ ...f, pageNumber: p })); };

  const totalPages   = Math.ceil(total / pageSize);
  const statusCounts = {
    Draft:     items.filter(a => a.workflowStatus === 'Draft').length,
    Submitted: items.filter(a => a.workflowStatus === 'Submitted').length,
    Confirmed: items.filter(a => a.workflowStatus === 'Confirmed').length,
    Reversed:  items.filter(a => a.workflowStatus === 'Reversed').length,
  };

  const columns: ListColumn<AllocationListDto>[] = [
    { key: 'no', header: 'Allocation #', className: 'font-mono text-xs font-medium text-brand-700 dark:text-brand-400', render: r => (
        <>
          {r.allocationNumber}
          {r.isReversal && <span className="ml-1.5 text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded-full">REV</span>}
        </>
      ) },
    { key: 'date',  header: 'Date', className: 'whitespace-nowrap text-slate-600 dark:text-slate-400', render: r => fmtDate(r.allocationDate) },
    { key: 'party', header: 'Customer', render: r => (
        <>
          <div className="font-medium text-slate-900 dark:text-white text-xs">{r.partyName}</div>
          <div className="text-[11px] text-slate-400 dark:text-gray-400">{r.partyCode}</div>
        </>
      ) },
    { key: 'ccy',   header: 'Currency', className: 'text-xs text-slate-600 dark:text-slate-400', render: r => r.currencyCode },
    { key: 'type',  header: 'Type', className: 'text-xs whitespace-nowrap text-slate-600 dark:text-slate-400', render: r => r.allocationType.replace(/([A-Z])/g, ' $1').trim() },
    { key: 'amt',   header: 'Total Allocated', align: 'right', className: 'font-mono font-medium whitespace-nowrap text-slate-900 dark:text-white', render: r => fmt(r.totalAllocated) },
    { key: 'st',    header: 'Status', render: r => (
        <span className={cn('px-2 py-0.5 rounded-full text-[11px] font-medium', STATUS_COLORS[r.workflowStatus])}>{STATUS_LABELS[r.workflowStatus]}</span>
      ) },
  ];

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <PageHeader
        icon={FileText}
        title="Customer Allocation"
        subtitle={`${total.toLocaleString()} allocation${total !== 1 ? 's' : ''}`}
        actions={
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Download size={13} />}
                onClick={() => exportToCsv('ar-allocations.csv', items.map((a: AllocationListDto) => ({
                  Number: a.allocationNumber, Date: a.allocationDate, PartyCode: a.partyCode,
                  PartyName: a.partyName, Currency: a.currencyCode, Type: a.allocationType,
                  TotalAllocated: a.totalAllocated, Status: a.workflowStatus,
                  Reversal: a.isReversal ? 'Yes' : 'No', CreatedBy: a.createdByName ?? '',
                })))}
              >
                Export
              </Button>
            )}
            <IconButton label="Refresh" icon={<RefreshCw size={15} />} onClick={() => void load(filter)} />
            {canCreate && (
              <Button leftIcon={<Plus size={15} />} onClick={() => navigate(AppRoutes.AR_ALLOCATION_NEW)}>New Allocation</Button>
            )}
          </div>
        }
      />

      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 pb-4">
        <ScopeChips chips={scope.chips} canReset={scope.canReset} onReset={scope.reset} />

        <StatusTiles
          active={filter.workflowStatus}
          onChange={handleStatusFilter}
          tiles={[
            { label: 'Draft',     count: statusCounts.Draft,     icon: <FileText     size={14} />, color: 'text-slate-500 dark:text-gray-400',   bg: 'bg-slate-50 dark:bg-slate-700/40',     ring: 'ring-slate-200',   status: 'Draft'     },
            { label: 'Submitted', count: statusCounts.Submitted, icon: <Clock        size={14} />, color: 'text-blue-600 dark:text-blue-400',    bg: 'bg-blue-50 dark:bg-blue-900/20',       ring: 'ring-blue-200',    status: 'Submitted' },
            { label: 'Confirmed', count: statusCounts.Confirmed, icon: <CheckCircle2 size={14} />, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', ring: 'ring-emerald-200', status: 'Confirmed' },
            { label: 'Reversed',  count: statusCounts.Reversed,  icon: <RotateCcw    size={14} />, color: 'text-amber-600 dark:text-amber-400',   bg: 'bg-amber-50 dark:bg-amber-900/20',     ring: 'ring-amber-200',   status: 'Reversed'  },
          ]}
        />

        <FilterRow>
          <ListSearch
            value={search}
            onChange={setSearch}
            onSubmit={handleSearch}
            placeholder="Search allocation #…"
          />

          <StatusPills
            statuses={['Draft', 'Submitted', 'Confirmed', 'Reversed', 'Cancelled'] as const}
            active={filter.workflowStatus}
            onChange={handleStatusFilter}
            label={st => STATUS_LABELS[st]}
            activeClass={st => STATUS_COLORS[st] ?? ''}
          />

          <DateRangeFilter
            from={filter.dateFrom}
            to={filter.dateTo}
            onChange={({ from, to }) => {
              setFilter(f => ({ ...f, dateFrom: from, dateTo: to, pageNumber: 1 }));
              setPage(1);
            }}
          />
        </FilterRow>
      </div>

      <div className="flex-1 overflow-auto">
        <ListTable
          rows={items}
          columns={columns}
          rowKey={r => r.id}
          onRowClick={r => navigate(AppRoutes.AR_ALLOCATION_DETAIL.replace(':id', r.id))}
          loading={loading}
          empty={{
            icon: FileText,
            message: 'No allocations found',
            action: undefined,
          }}
        />
      </div>

      {totalPages > 1 && (
        <ListPager
          page={page}
          totalPages={totalPages}
          totalCount={total}
          onChange={handlePageChange}
          noun="allocations"
        />
      )}
    </div>
  );
};
